import { Component, OnInit, Output, EventEmitter, Input, ViewChild, SimpleChanges } from '@angular/core';
import { FormGroup, FormBuilder } from '@angular/forms';
import { CampaignsService } from 'src/app/pages/campaign-module/campaigns/campaigns.service';
import { debounceTime, filter, distinctUntilChanged, map, takeUntil, finalize } from 'rxjs/operators';
import { MatAutocomplete, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { DialogService, LanguageService } from 'src/app/services';
import { environment } from 'src/environments/environment';
import { of as observableOf, fromEvent } from 'rxjs';
import * as _ from 'lodash';

@Component({
  selector: 'esc-distribution-selector',
  templateUrl: './distribution-selector.component.html',
  styleUrls: ['./distribution-selector.component.scss']
})
export class DistributionSelectorComponent implements OnInit {
  @Input() textLabel;
  @Input() wrapperLayoutAlign;
  @Input() labelFlex;
  @Input() formWrapperFlex;
  @Input() campaignData;
  @Input() isDisabled;
  @Output() distributionSelected = new EventEmitter;
  @Output() errorLoading = new EventEmitter;
  @Output() loadingDistributionData = new EventEmitter;
  
  @ViewChild('distributionAC') distributionACRef: MatAutocomplete;
  @ViewChild(MatAutocompleteTrigger) distributionACTr: MatAutocompleteTrigger;
  
  form: FormGroup;

  loading = {
    distribution: false,
    init: false,
    more: false,
    error: false
  };
  public listData = [];
  private currentFilters;
  networkErrMsg = this.languageService.getTranslation("MSG_ERROR_NETWORK_CONNECTION", {});

  constructor(
    private fb: FormBuilder,
    public campSvc: CampaignsService,
    private dialogService: DialogService,
    private languageService: LanguageService,
    ) { }

  ngOnInit() {
    this.currentFilters = _.cloneDeep(INITIAL_OPTIONS);
    this.initForm();
    this.detectFormChanges();
  }

  ngOnChanges(changes: SimpleChanges) {
    if(changes.campaignData && changes.campaignData.currentValue && this.form) {
      this.form.get('distribution').reset(null, {emitEvent: false});
    }

    if(_.get(changes, 'isDisabled.currentValue') && this.form) {
      this.form.disable();
    } else if (!_.get(changes, 'isDisabled.currentValue') && this.form) {
      this.form.enable();
    }
  }

  initForm() {
    this.form = this.fb.group({
      distribution: null
    });

    if(_.has(this.campaignData, 'campaign_id')) {
      let distributionObj = {
        id: _.get(this.campaignData, 'distribution_id'),
        code: _.get(this.campaignData, 'distribution_code'),
        description: _.get(this.campaignData, 'distribution_description'),
      }
      this.form.get('distribution').setValue(distributionObj);
      this.getListData('edit');
    }

    if(this.isDisabled) {
      // console.log(this.isDisabled)
      this.form.disable();
    } else {
      this.form.enable();
    }
  }

  detectFormChanges() {
    this.form.get('distribution').valueChanges.pipe(
      debounceTime(600),
      filter((term) => !_.isObject(term)),
      // distinctUntilChanged(),
      map((term: string) => _.trim(term)),
    )
    .subscribe(value => {
      this.currentFilters.web_filter.keyword = _.toString(value);
      this.listData = [];
      this.getListData(null);
    });
  }

  getInitialValues(data) {
    this.currentFilters.web_filter.offset = 0;
    this.currentFilters.web_filter.keyword = "";
    this.listData = [];
    this.getListData(null);
  }
   
  getMoreData(name) {
    if (this.currentFilters.getMore) {
      this.currentFilters.web_filter.offset += INITIAL_OPTIONS.web_filter.limit;
      this.getListData(name);
    }
  }

  getListData(mode) {
    this.loading.distribution = true;
    if (this.loading.init == true) {
      return;
    }
    this.loading.error = false;
    if (this.listData.length == 0) {
      this.loading.init = true;
    } else {
      this.loading.more = true;
    }

    if(_.has(this.campaignData, 'id') || _.has(this.campaignData, 'campaign_id')) {
      this.currentFilters.web_filter.campaign_id = _.get(this.campaignData, 'id') || _.get(this.campaignData, 'campaign_id');
    }

    this.campSvc.getDistributions(this.currentFilters)
      .subscribe((res: any) => {
        if (environment.showLogs) {
          console.log(res);
        }
        this.loading.distribution = false;
        this.loading.init = false;
        this.loading.more = false;
        if (res == null || res.status == 0 || res.status == 400 || res.status == 500 || res.status == 502 || res.status == 504) {
          this.loading.error = true;
          this.listData = [];
          this.dialogService.openSnackBar(this.networkErrMsg)
          return;
        }
        if (_.has(res, 'value') && (res.value == "Success")) {
          this.listData = this.listData.concat(res.data);

          if(mode == 'edit') {
            // set selected distribution
            let selectedDistribution = this.listData.filter(e => {
              return e.id == _.get(this.form.get('distribution'), 'value.id')
            })
            this.form.get('distribution').setValue(selectedDistribution[0]);
            this.getDistributionDetail('edit');
          }

          if((res.data.length > 0) && (this.listData.length < _.get(res, 'count'))) {
            this.currentFilters.getMore = true;
          }
        }
      }, err => {
        if (environment.showLogs) {
          console.error(err);
        }
        this.loading.distribution = false;
        if (this.listData.length == 0) {
          this.loading.error = true;
        } else {
          this.dialogService.openSnackBar(this.languageService.getTranslation("MSG_INTERNAL_SERVER_ERROR", {}));
          this.currentFilters.web_filter.limit -= this.currentFilters.web_filter.offset;
        }
        return observableOf(null);
      });
  }

  displayFn(data?: any): string | undefined {
    if(data) {
      return _.get(data, 'code') + ' - ' + _.get(data, 'description');
    }
    return null;
  }

  autoCmpltClosed() {
    this.listData = [];
    
    if (_.isObject(_.get(this.form.get('distribution'), 'value'))) {
      this.getDistributionDetail(null);
    }
  }

  getDistributionDetail(mode) {
    this.loadingDistributionData.next(true);
    if (this.loading.init == true) {
      return;
    }
    this.loading.error = false;
    if (this.listData.length == 0) {
      this.loading.init = true;
    } else {
      this.loading.more = true;
    }

    this.currentFilters.web_filter.distribution_id = _.get(this.form.get('distribution'), 'value.id');

    this.campSvc.getDistributionDetail(this.currentFilters)
      .pipe(finalize(() => {
        this.loadingDistributionData.next(false);
      }))
      .subscribe((res: any) => {
        if (environment.showLogs) {
          console.log(res);
        }
        this.loading.init = false;
        this.loading.more = false;
        if (res == null || res.status == 0 || res.status == 400 || res.status == 500 || res.status == 502 || res.status == 504) {
          this.loading.error = true;
          this.listData = [];
          this.dialogService.openSnackBar(this.networkErrMsg)
          if(mode == 'edit') { 
            this.errorLoading.next(true);
          }
          return;
        }
        if (_.has(res, 'value') && (res.value == "Success")) {
          let distributionDetail = Object.assign(_.get(this.form.get('distribution'), 'value'), _.get(res, 'data'));

          if (_.isObject(_.get(this.form.get('distribution'), 'value'))) {
            this.distributionSelected.next(distributionDetail);
          }
        } 
      }, err => {
        if (environment.showLogs) {
          console.error(err);
        }
        this.loadingDistributionData.next(false);
        if (this.listData.length == 0) {
          this.loading.error = true;
        } else {
          this.dialogService.openSnackBar(this.languageService.getTranslation("MSG_INTERNAL_SERVER_ERROR", {}));
          this.currentFilters.web_filter.limit -= this.currentFilters.web_filter.offset;
        }
        return observableOf(null);
      });
  }

  clearForm(ev: Event) {
    this.form.get('distribution').setValue(null, { emitEvent: false });
    this.distributionSelected.next(null);
    // ev.stopPropagation();
  }

  autocompleteScroll(name, refName, acTrigger) {
    setTimeout(() => {
      if (this[refName].isOpen || this[refName].panel) {
        fromEvent(this[refName].panel.nativeElement, 'scroll')
          .pipe(
            map(x => this[refName].panel.nativeElement.scrollTop),
            takeUntil(this[acTrigger].panelClosingActions)
          )
          .subscribe(x => {
            const scrollTop = this[refName].panel.nativeElement
              .scrollTop;
            const scrollHeight = this[refName].panel.nativeElement
              .scrollHeight;
            const elementHeight = this[refName].panel.nativeElement
              .clientHeight;
            const atBottom = scrollHeight === scrollTop + elementHeight;
            if (atBottom) {
              // fetch more data
              this.getMoreData(name);
            }
          });
      }
    });
  }
}

const INITIAL_OPTIONS = {
  web_filter: {
    offset: 0,
    limit: 50,
    keyword: "",
    status: "All",
    campaign_id: 0,
    distribution_id: 0,
    is_asc: 1,
    getMore: true
  }
}